import ToastActions from 'actions/ToastActions';

import React, { useState, ReactNode } from 'react';
import { compose, graphql } from 'react-apollo';
import { safePromise } from 'utils/tsUtils';
import { format } from 'lib/phoneNumbers';
import Icon from 'components/Icon';

import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
import ClipboardTrigger from 'components/ClipboardTrigger';
import TooltipTrigger from 'components/TooltipTrigger';
import Tooltip from 'components/Tooltip';

import { AdminRemoveContactInfoFromUserInput, MediumEnum } from 'globalTypes';
import AccordionTextWithIconItem from 'components/AccordionBodyItem/AccordionTextWithIconItem';
import Glyph from 'components/Glyph';

import { apiAdminRemoveContactInfoFromUser } from '../RemoveContactInfo/__generated__/apiAdminRemoveContactInfoFromUser';
import { apiContactInfoForUser, apiContactInfoForUser_api_contactInfoForUser } from '../__generated__/apiContactInfoForUser';
import { userProfileScreen } from '../__generated__/userProfileScreen';

import API_ADMIN_REMOVE_CONTACT_INFO_FROM_USER from '../RemoveContactInfo/apiAdminRemoveContactInfoFromUser.gql';
import API_CONTACT_INFO_FOR_USER from '../apiContactInfoForUser.gql';
import USER_PROFILE_SCREEN from '../userProfileScreen.gql';

import RemoveContactInfoDialog from '../RemoveContactInfo/RemoveContactInfoDialog';

import './ContactInfoDevice.scss';

export type ContactInfoDeviceProps = {
  deviceId: number;
  deviceEndpoint: string;
  relatedUserUuid: string;
  organizationUuid: string;
  onComplete: () => void;
  onEdit?: (device: apiContactInfoForUser_api_contactInfoForUser) => void;
  onError: (errorMessage: string) => void;
  fallback: ReactNode;
  trackEvent: (value: string, object?: Object) => void;
};

const ContactInfoDevice = (params: ContactInfoDeviceProps): JSX.Element => {
  const props = params as ContactInfoDeviceProps & {
    userProfileScreen: { loading: boolean; error: Error } & userProfileScreen;
    contactInfoForUser: { loading: boolean; error: Error } & apiContactInfoForUser;
    removeContactInfo: (value: {
      variables: { input: AdminRemoveContactInfoFromUserInput };
    }) => Promise<{ data: apiAdminRemoveContactInfoFromUser }>;
  };

  const { error: errorUserProfileScreen, userProfileScreen } = props.userProfileScreen;
  const { error: errorContactInfoForUser, api_contactInfoForUser } = props.contactInfoForUser;

  const currentDevice = api_contactInfoForUser?.find(device => device.id === props.deviceId);

  const [showActions, setShowActions] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [hovering, setHovering] = useState<boolean>(false);

  const handleRemoveDevice = async () => {
    if (!currentDevice || !userProfileScreen?.canAddDevice) {
      return;
    }

    setMutationLoading(true);
    const [result, error] = await safePromise(
      props.removeContactInfo({
        variables: {
          input: {
            deviceUuid: currentDevice.uuid,
            userUuid: props.relatedUserUuid,
            organizationUuid: props.organizationUuid,
          },
        },
      })
    );
    setMutationLoading(false);

    if (error) {
      return props.onError(error.message);
    }

    if (result?.data?.api_adminRemoveContactInfoFromUser?.success) {
      setShowRemove(false);
      return props.onComplete();
    }

    if (result?.data?.api_adminRemoveContactInfoFromUser?.errors) {
      props.onError(result.data.api_adminRemoveContactInfoFromUser.errors[0].message);
    }
  };

  if (errorUserProfileScreen || errorContactInfoForUser || !userProfileScreen || !currentDevice) {
    return <> {props.fallback} </>;
  }

  const handleDeviceCopy = () => {
    ToastActions.showSuccessToast('Contact information copied to clipboard');
    props.trackEvent('user_profile.device.copied');
  };
  
  const handleEditDevice = () => {
    if (props.onEdit) {
      props.onEdit(currentDevice);
    }
  };

  const onItemHover = () => {
    setHovering(true);
  }
  
  const onLeaveHover = () => {
    setHovering(false);
    setShowActions(false);
  }

  const isEmail = currentDevice.medium === MediumEnum.email;

  let actionDots = undefined;
  let actions = undefined;

  if (hovering) {
    actionDots = (
      <TooltipTrigger
        placement="left"
        tooltip={
          <Tooltip>
            More actions
          </Tooltip>
        }>
        <Glyph name="ellipses" size="xsmall" solo />
      </TooltipTrigger>
    );
    if (showActions) {
      actions = (
        <Menu rowHover className="ContactInfoDeviceActionsMenu Actions-placementRight Actions-placementTop">
          <MenuItem menuKey="contact_info_copy_device_to_clipboard">
            <Icon name="clipboard" size="medium" />
            <ClipboardTrigger text={props.deviceEndpoint} onCopy={handleDeviceCopy}>
              <span> Copy to clipboard</span>
            </ClipboardTrigger>
          </MenuItem>
          <MenuItem menuKey="contact_info_remove_device" onClick={handleEditDevice}>
            <Glyph name="pencil-stroke" />
            <span>Edit</span>
          </MenuItem>
          <MenuItem menuKey="contact_info_remove_device" onClick={() => setShowRemove(true)}>
            <Glyph name="close" />
            <span>Remove</span>
          </MenuItem>
        </Menu>
      );
    }
  }  

  return (
    <div className="ContactInfoDeviceContainer"
      onMouseEnter={onItemHover} 
      onMouseLeave={onLeaveHover}>
      <AccordionTextWithIconItem
        icon={isEmail ? 'mail' : 'phone'}
        title={isEmail ? currentDevice.uid : format(currentDevice.uid)}
        onClick={() => {
          if (userProfileScreen.canAddDevice) {
            setShowActions(!showActions);
          }
        }}
        action={actionDots}
        textClassName="AccordionBodyItem--text-wrap"
      />
      {
        showActions && actions
      }

      {
        showRemove && <RemoveContactInfoDialog
          name={userProfileScreen.identification.fullName}
          isRostered={currentDevice.rostered}
          deviceUid={currentDevice.uid}
          onRequestHide={() => setShowRemove(false)}
          onRemoveRequest={handleRemoveDevice}
          onEditRequest={handleEditDevice}
          loading={mutationLoading}
          isOnlyDevice={api_contactInfoForUser?.length === 1}
        />
      }
    </div>
  );
};

const ContactInfoDeviceCompose = compose(
  graphql(USER_PROFILE_SCREEN, {
    name: 'userProfileScreen',
    options: (props: any) => ({
      variables: {
        input: {
          relatedUserUuid: props.relatedUserUuid,
          organizationUuid: props.organizationUuid,
        },
      },
    }),
  }),
  graphql(API_CONTACT_INFO_FOR_USER, {
    name: 'contactInfoForUser',
    options: (props: any) => ({
      variables: {
        targetUserUuid: props.relatedUserUuid,
      },
    }),
  }),
  graphql(API_ADMIN_REMOVE_CONTACT_INFO_FROM_USER, { name: 'removeContactInfo' })
)(ContactInfoDevice) as typeof ContactInfoDevice;

export default ContactInfoDeviceCompose;
